import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { LandingPage as LandingPageEnum } from '../../../ClientAccess/ClientAccess.types';
import { ClientAccessSettings } from '../../../ClientAccess/components/ClientAccessSettings/ClientAccessSettings';
import {
  defaultDashboardAccessSelector,
  hasCreditMonitoringSelector,
  hasDataAggregationSelector,
} from '../../../AdviserArea/AdviserAreaSelector';
import { Toggle } from '../../../CommonComponents/OPL/Toggle/Toggle';
import { Separator } from '../../../CommonComponents/OPL/Separator/Separator';
import { LandingPage } from '../../../ClientAccess/components/LandingPage/LandingPage';
import {
  getIsLandingPageValid,
  getIsTogglingDashboard,
} from '../../../ClientAccess/components/LandingPage/useLandingPage';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { useUpsertCampaignMutation } from '../../api/useUpsertCampaignMutation';
import { FORM_ID, FormValues, SAVE_BUTTON_TEXT, validation } from './CampaignSettings.meta';
import { CampaignAccess } from './components/CampaignAccess/CampaignAccess';
import { CampaignDTO, CampaignsDTO, ClientAccess } from '../../api/useCampaignsQuery';
import { CampaignAccessDTO } from '../../api/useCampaignAccessQuery';
import { useAgreementOptionsQuery } from '../../api/useAgreementOptionsQuery';
import { landingPageNameMap } from '../../../ClientAccess/components/LandingPage/LandingPage.meta';

interface Props {
  campaignsData: CampaignsDTO;
  campaign: CampaignDTO;
  onClose(): void;
}

export const CampaignSettings: React.FC<Props> = ({ campaignsData, campaign, onClose }) => {
  const [currentCampaign, setCurrentCampaign] = useState(campaign);

  const [campaignAccess, setCampaignAccess] = useState<CampaignAccessDTO | null>(null);

  const [showLandingPageAlert, setShowLandingPageAlert] = useState(false);

  const {
    clientAccess: {
      onboardingWorkflow,
      dashboardAccess,
      linkAccountsAccess,
      taxableAccountModelGroup,
      nonTaxableAccountModelGroup,
      fullyAutomatedAccountOpening,
      clientLandingPage,
      creditMonitoringAccess,
      selectedAgreements,
    },
  } = currentCampaign;

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<FormValues>({
    defaultValues: { name: campaign.name, isActive: campaign.isActive },
    resolver: yupResolver(validation),
  });

  const {
    onboardingWorkflowOptions,
    defaultClientLandingPage,
    clientLandingPageOptions,
    hasBeFi20,
    availableLeadsAccess,
  } = campaignsData;

  const {
    data: agreementOptions,
    isLoading: isLoadingAgreementOptions,
    isError: isErrorAgreementOptions,
  } = useAgreementOptionsQuery({ campaignId: campaign.id || null });

  const {
    mutateAsync: upsertCampaign,
    isLoading: isUpsertingCampaign,
    error: upsertCampaignError,
    reset: resetUpsertCampaignMutation,
  } = useUpsertCampaignMutation();

  const showCreditMonitoring = useSelector(hasCreditMonitoringSelector);
  const showDataAggregation = useSelector(hasDataAggregationSelector);
  const defaultDashboardAccessSet = useSelector(defaultDashboardAccessSelector);

  const onClientAccessChange = (newClientAccess: Partial<ClientAccess>) => {
    const mergedClientAccess = {
      ...currentCampaign.clientAccess,
      ...newClientAccess,
    };

    const isTogglingDashboard = getIsTogglingDashboard({
      oldDashboardAccess: dashboardAccess,
      newDashBoardAccess: mergedClientAccess.dashboardAccess,
    });

    const isLandingPageValid = getIsLandingPageValid({
      clientLandingPage: mergedClientAccess.clientLandingPage,
      dashboardAccess: mergedClientAccess.dashboardAccess,
      creditMonitoringAccess: mergedClientAccess.creditMonitoringAccess,
      landingPageOptions: clientLandingPageOptions,
    });

    if (!isLandingPageValid && !isTogglingDashboard) {
      setShowLandingPageAlert(true);
    } else {
      setShowLandingPageAlert(false);
    }

    return setCurrentCampaign({
      ...currentCampaign,
      clientAccess: {
        ...mergedClientAccess,
        clientLandingPage: !isLandingPageValid
          ? isTogglingDashboard
            ? null
            : LandingPageEnum.Overview
          : mergedClientAccess.clientLandingPage,
      },
    });
  };

  const onSaveCampaign = async (formValues: FormValues) => {
    try {
      await upsertCampaign({
        campaign: { ...currentCampaign, ...formValues },
        sharedAccess: campaignAccess?.relationships,
      });

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader hasBorder>Campaign</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody
        as="form"
        id={FORM_ID}
        onSubmit={handleSubmit(onSaveCampaign)}
        css="overflow-y: scroll; padding-top: 24px"
      >
        {showLandingPageAlert && (
          <Alert
            message={`Changing this setting will now default the client portal landing page to ${
              landingPageNameMap[LandingPageEnum.Overview]
            }`}
            variant="warning"
            onRequestClose={() => setShowLandingPageAlert(false)}
            mb="16"
          />
        )}

        {isErrorAgreementOptions && (
          <Alert
            message="There was an error with retrieving agreements. Please try again later."
            variant="error"
            mb="16"
          />
        )}

        {upsertCampaignError && (
          <Alert
            message={upsertCampaignError.message}
            variant="error"
            onRequestClose={resetUpsertCampaignMutation}
            mb="16"
          />
        )}

        <Input
          {...register('name')}
          label="Campaign Name"
          formVariant={formErrors.name ? 'error' : undefined}
          helperText={formErrors.name?.message}
        />

        <ClientAccessSettings
          onboardingWorkflow={onboardingWorkflow}
          onboardingWorkflowOptions={onboardingWorkflowOptions}
          dashboardAccess={dashboardAccess}
          defaultDashboardAccessSet={defaultDashboardAccessSet}
          linkAccountsAccess={linkAccountsAccess}
          creditMonitoringAccess={creditMonitoringAccess}
          taxableAccountModelGroup={taxableAccountModelGroup}
          nonTaxableAccountModelGroup={nonTaxableAccountModelGroup}
          fullyAutomatedAccountOpening={fullyAutomatedAccountOpening}
          showDataAggregation={showDataAggregation}
          showCreditMonitoring={showCreditMonitoring}
          showBeFi20={hasBeFi20}
          agreementOptions={agreementOptions}
          selectedAgreements={selectedAgreements}
          isLoadingAgreementOptions={isLoadingAgreementOptions}
          onChange={onClientAccessChange}
        />

        <Separator my="16" />

        <Toggle {...register('isActive')} variant="setting" my="16">
          Active
        </Toggle>

        <Separator my="16" />

        <LandingPage
          displayName="campaign"
          clientLandingPage={clientLandingPage}
          defaultClientLandingPage={defaultClientLandingPage}
          clientLandingPageOptions={clientLandingPageOptions}
          dashboardAccess={dashboardAccess}
          creditMonitoringAccess={creditMonitoringAccess}
          onChange={({ clientLandingPage: landingPage }) => {
            setCurrentCampaign({
              ...currentCampaign,
              clientAccess: {
                ...currentCampaign.clientAccess,
                clientLandingPage: landingPage,
              },
            });
          }}
          my="16"
        />

        <CampaignAccess
          campaign={currentCampaign}
          setCampaign={setCurrentCampaign}
          campaignAccess={campaignAccess}
          availableLeadsAccess={availableLeadsAccess}
          setCampaignAccess={setCampaignAccess}
        />
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button type="submit" form={FORM_ID} isLoading={isUpsertingCampaign} ml="8">
          {SAVE_BUTTON_TEXT}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
